<template>
  <div class="customer-income-wrapper">
    <div v-if="showExport" class="d-flex justify-end mb-2">
      <ExportButton @click="exportData" tooltip="Export to CSV" />
    </div>
    
    <apexchart
      type="bar"
      :options="chartOptions"
      :series="series"
      height="280"
    ></apexchart>

    <v-table density="comfortable" class="mt-4">
      <thead>
        <tr class="bg-grey-lighten-3">
          <th class="text-left font-weight-bold">Income per Year</th>
          <th class="text-center font-weight-bold">Tax Rate</th>
          <th class="text-right font-weight-bold">Total</th>
          <th class="text-right font-weight-bold">&lt; 30</th>
          <th class="text-right font-weight-bold">30-44</th>
          <th class="text-right font-weight-bold">45-54</th>
          <th class="text-right font-weight-bold">&gt;= 55</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, index) in displayData" :key="index" class="hover-row">
          <td class="font-weight-medium">{{ item.incomeRange }}</td>
          <td class="text-center">
            <v-chip size="small" color="primary" variant="tonal" label>{{ item.taxRate }}</v-chip>
          </td>
          <td class="text-right font-weight-bold">{{ item.totalUsers > 0 ? formatNumber(item.totalUsers) : '-' }}</td>
          <td class="text-right text-body-2">{{ item.ageLessThan30 > 0 ? formatNumber(item.ageLessThan30) : '-' }}</td>
          <td class="text-right text-body-2">{{ item.age30to44 > 0 ? formatNumber(item.age30to44) : '-' }}</td>
          <td class="text-right text-body-2">{{ item.age45to54 > 0 ? formatNumber(item.age45to54) : '-' }}</td>
          <td class="text-right text-body-2">{{ item.ageGreaterEqual55 > 0 ? formatNumber(item.ageGreaterEqual55) : '-' }}</td>
        </tr>
      </tbody>
    </v-table>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import ExportButton from '@/components/TaxDashboard/ExportButton.vue';
import mockData from '@/services/mock/taxDashboardMockData.json';
import { exportToCSV } from '@/utils/csvExport';

export default {
  name: 'CustomerIncomeTable',
  components: {
    apexchart: VueApexCharts,
    ExportButton,
  },
  props: {
    data: {
      type: Array,
      required: true
    },
    showExport: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    // Use income ranges from mock data
    fixedIncomeRanges() {
      return mockData.incomeRanges;
    },
    // Merge fixed ranges with data by array index
    displayData() {
      return this.fixedIncomeRanges.map((fixed, idx) => {
        const dataItem = this.data[idx];
        if (!dataItem) {
          return {
            ...fixed,
            totalUsers: 0,
            ageLessThan30: 0,
            age30to44: 0,
            age45to54: 0,
            ageGreaterEqual55: 0
          };
        }
        return {
          ...fixed,
          ...dataItem
        };
      });
    },
    series() {
      return [
        {
          name: '< 30',
          data: this.displayData.map(item => item.ageLessThan30 || 0)
        },
        {
          name: '30-44',
          data: this.displayData.map(item => item.age30to44 || 0)
        },
        {
          name: '45-54',
          data: this.displayData.map(item => item.age45to54 || 0)
        },
        {
          name: '>= 55',
          data: this.displayData.map(item => item.ageGreaterEqual55 || 0)
        }
      ];
    },
    chartOptions() {
      return {
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 4,
            columnWidth: '60%'
          }
        },
        xaxis: {
          categories: this.displayData.map(item => item.incomeRange),
          labels: {
            rotate: -45,
            rotateAlways: true,
            style: {
              fontSize: '10px'
            }
          }
        },
        yaxis: {
          title: {
            text: 'Number of Users',
            style: {
              fontSize: '12px',
              fontWeight: 600
            }
          }
        },
        legend: {
          position: 'top',
          horizontalAlign: 'center',
          fontSize: '12px'
        },
        colors: ['#1976D2', '#43A047', '#FB8C00', '#E53935'],
        dataLabels: {
          enabled: false
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val.toLocaleString('en-US') + ' users';
            }
          }
        },
        grid: {
          borderColor: '#f1f1f1'
        }
      };
    }
  },
  methods: {
    formatNumber(num) {
      return num?.toLocaleString('en-US') || '0';
    },
    exportData() {
      const headers = [
        { key: 'incomeRange', label: 'Income per Year' },
        { key: 'taxRate', label: 'Tax Rate' },
        { key: 'totalUsers', label: 'Total Users' },
        { key: 'ageLessThan30', label: '< 30' },
        { key: 'age30to44', label: '30-44' },
        { key: 'age45to54', label: '45-54' },
        { key: 'ageGreaterEqual55', label: '>= 55' }
      ];
      exportToCSV(this.displayData, headers, 'customer_income_distribution');
    }
  }
};
</script>

<style scoped>
.customer-income-wrapper {
  width: 100%;
}

.hover-row {
  transition: background-color 0.2s ease;
}

.hover-row:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

thead tr th {
  font-size: 0.75rem;
  padding: 12px 8px;
  border-bottom: 2px solid rgba(0, 0, 0, 0.12);
}

tbody tr td {
  padding: 12px 8px;
  font-size: 0.813rem;
}
</style>
